import { Check, Undo2, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { IconButton } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Select } from '@/components/ui/Select'
import { sectionLabel } from '@/features/academic/sectionLabel'
import type { SchoolClass } from '@/features/academic/types'
import { cn } from '@/utils/cn'
import { DECISIONS, decisionLabel, formatScore, requestTone, statusLabel, statusTone } from '../labels'
import { effectiveRow, needsPlace } from '../rows'
import type { BoardRow, DraftRow, PlacementDecision, SeatSummary } from '../types'

interface Props {
  rows: BoardRow[]
  drafts: Record<number, DraftRow>
  onChange: (studentId: number, value: DraftRow) => void
  selected: Set<number>
  onToggle: (studentId: number) => void
  onToggleAll: () => void
  classes: SchoolClass[]
  seats: SeatSummary[]
  canManage: boolean
  onConfirm: (placementId: number) => void
  onUnconfirm: (placementId: number) => void
  onCancel: (placementId: number) => void
}

function useRowParts(props: Props) {
  const { t } = useTranslation('promotion')
  const seatsById = new Map(props.seats.map((s) => [s.sectionId, s]))
  const classOptions = props.classes.map((c) => ({ value: c.id, label: c.name }))

  const resultCell = (row: BoardRow) =>
    row.result ? (
      <div className="min-w-0">
        <p className="font-medium text-ink tabular-nums">{formatScore(row.result)}</p>
        <p className={cn('text-xs', row.result.isPass ? 'text-success-700' : 'text-danger-700')}>
          {row.result.isPass ? t('Passed') : t('Not passed')}
          {row.result.grade ? ` · ${row.result.grade}` : ''}
        </p>
      </div>
    ) : (
      <span className="text-xs text-muted">{t('No published result')}</span>
    )

  const requestCell = (row: BoardRow) => {
    const r = row.placement?.request
    if (!r || r.status === 'none') return <span className="text-muted">—</span>
    return (
      <div className="min-w-0 space-y-0.5">
        {/* Long class names wrap inside the badge instead of widening the page on phones. */}
        <Badge tone={requestTone[r.status]} size="sm" className="max-w-full whitespace-normal">
          {r.status === 'requested'
            ? r.requestedClass
              ? t('Re-enrolment requested — {{class}}', { class: r.requestedClass.name })
              : t('Re-enrolment requested')
            : t('Not returning')}
        </Badge>
        {r.note ? <p className="line-clamp-2 max-w-56 text-xs text-muted">“{r.note}”</p> : null}
      </div>
    )
  }

  const editors = (row: BoardRow) => {
    const value = effectiveRow(row, props.drafts[row.studentId])
    const locked = !props.canManage || row.placement?.status === 'applied'
    const cls = props.classes.find((c) => c.id === value.targetClassId)
    const set = (patch: Partial<DraftRow>) => props.onChange(row.studentId, { ...value, ...patch })
    const decision = (
      <Select
        size="sm"
        aria-label={t('Decision for {{name}}', { name: row.fullName })}
        value={value.decision}
        disabled={locked}
        placeholder={t('To decide')}
        options={DECISIONS.map((d) => ({ value: d, label: decisionLabel(t, d) }))}
        onChange={(e) => {
          const d = e.target.value as PlacementDecision | ''
          set(
            needsPlace(d)
              ? {
                  decision: d,
                  targetClassId: d === 'repeat' ? row.current.classId : value.targetClassId,
                  targetSectionId: d === 'repeat' ? null : value.targetSectionId,
                }
              : { decision: d, targetClassId: null, targetSectionId: null },
          )
        }}
      />
    )
    const klass = (
      <Select
        size="sm"
        aria-label={t('Next class for {{name}}', { name: row.fullName })}
        value={value.targetClassId ?? ''}
        disabled={locked || !needsPlace(value.decision)}
        placeholder={needsPlace(value.decision) ? t('Choose a class') : '—'}
        options={classOptions}
        onChange={(e) =>
          set({ targetClassId: e.target.value ? Number(e.target.value) : null, targetSectionId: null })
        }
      />
    )
    const section = (
      <Select
        size="sm"
        aria-label={t('Next section for {{name}}', { name: row.fullName })}
        value={value.targetSectionId ?? ''}
        disabled={locked || !cls}
        placeholder={cls ? t('To dispatch') : '—'}
        options={(cls?.sections ?? []).map((s) => {
          const seat = seatsById.get(s.id)
          const taken = seat ? seat.enrolled + seat.confirmed : 0
          const label = sectionLabel(cls?.name, s.name)
          return {
            value: s.id,
            label: s.capacity !== null ? `${label} (${taken}/${s.capacity})` : label,
          }
        })}
        onChange={(e) => set({ targetSectionId: e.target.value ? Number(e.target.value) : null })}
      />
    )
    return { decision, klass, section, value }
  }

  const statusCell = (row: BoardRow) => {
    const dirty = props.drafts[row.studentId] !== undefined
    return (
      <div className="flex flex-wrap items-center gap-1">
        {row.placement ? (
          <Badge tone={statusTone[row.placement.status]} size="sm" dot>
            {statusLabel(t, row.placement.status)}
          </Badge>
        ) : (
          <Badge tone="neutral" size="sm">
            {t('To decide')}
          </Badge>
        )}
        {dirty ? (
          <Badge tone="warning" size="sm">
            {t('Unsaved')}
          </Badge>
        ) : null}
      </div>
    )
  }

  const actions = (row: BoardRow) => {
    const p = row.placement
    if (!props.canManage || !p || p.status === 'applied' || props.drafts[row.studentId]) return null
    return (
      <div className="flex justify-end gap-1">
        {p.status === 'planned' ? (
          <IconButton
            size="icon-sm"
            label={t('Confirm')}
            icon={<Check />}
            onClick={() => props.onConfirm(p.id)}
          />
        ) : null}
        {p.status === 'confirmed' || p.status === 'cancelled' ? (
          <IconButton
            size="icon-sm"
            label={t('Back to planned')}
            icon={<Undo2 />}
            onClick={() => props.onUnconfirm(p.id)}
          />
        ) : null}
        {p.status !== 'cancelled' ? (
          <IconButton
            size="icon-sm"
            label={t('Cancel the placement')}
            icon={<XCircle />}
            onClick={() => props.onCancel(p.id)}
          />
        ) : null}
      </div>
    )
  }

  return { t, resultCell, requestCell, editors, statusCell, actions }
}

/** The planning table (cards on phones): one row per student of the year. */
export function PlacementTable(props: Props) {
  const { t, resultCell, requestCell, editors, statusCell, actions } = useRowParts(props)
  const { rows, selected } = props
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.studentId))
  const someSelected = rows.some((r) => selected.has(r.studentId))

  const who = (row: BoardRow) => (
    <div className="flex min-w-0 items-center gap-2.5">
      <Avatar name={row.fullName} src={row.photoUrl} size="sm" />
      <div className="min-w-0">
        <p className="truncate font-medium text-ink">{row.fullName}</p>
        <p className="truncate text-xs text-muted">
          {sectionLabel(row.current.className, row.current.sectionName)} · {row.studentCode}
        </p>
      </div>
    </div>
  )

  return (
    <>
      <ul className="divide-y divide-line md:hidden" aria-label={t('Students')}>
        {rows.map((row) => {
          const e = editors(row)
          return (
            <li key={row.studentId} className="space-y-3 p-4">
              <div className="flex items-start gap-3">
                {props.canManage ? (
                  <Checkbox
                    className="mt-2"
                    checked={selected.has(row.studentId)}
                    onCheckedChange={() => props.onToggle(row.studentId)}
                    aria-label={t('Select {{name}}', { name: row.fullName })}
                  />
                ) : null}
                <div className="min-w-0 flex-1">{who(row)}</div>
                {statusCell(row)}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {resultCell(row)}
                {requestCell(row)}
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                {e.decision}
                {e.klass}
                {e.section}
              </div>
              {actions(row)}
            </li>
          )
        })}
      </ul>
      <div className="hidden scrollbar-thin overflow-x-auto md:block">
        <table className="w-full text-sm">
          <caption className="sr-only">{t('Next-year placements')}</caption>
          <thead>
            <tr className="border-b border-line text-left text-xs font-medium text-muted">
              {props.canManage ? (
                <th className="w-10 px-4 py-3">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                    onCheckedChange={props.onToggleAll}
                    aria-label={t('Select all')}
                  />
                </th>
              ) : null}
              <th className="px-3 py-3">{t('Student')}</th>
              <th className="px-3 py-3">{t('Result')}</th>
              <th className="px-3 py-3">{t('Family request')}</th>
              <th className="min-w-36 px-3 py-3">{t('Decision')}</th>
              <th className="min-w-36 px-3 py-3">{t('Next class')}</th>
              <th className="min-w-36 px-3 py-3">{t('Next section')}</th>
              <th className="px-3 py-3">{t('Status')}</th>
              <th className="px-3 py-3">
                <span className="sr-only">{t('Actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const e = editors(row)
              return (
                <tr
                  key={row.studentId}
                  className={cn(
                    'border-b border-line align-top last:border-0',
                    selected.has(row.studentId) && 'bg-brand-50/40',
                  )}
                >
                  {props.canManage ? (
                    <td className="px-4 py-3">
                      <Checkbox
                        checked={selected.has(row.studentId)}
                        onCheckedChange={() => props.onToggle(row.studentId)}
                        aria-label={t('Select {{name}}', { name: row.fullName })}
                      />
                    </td>
                  ) : null}
                  <td className="max-w-56 px-3 py-3">{who(row)}</td>
                  <td className="px-3 py-3">{resultCell(row)}</td>
                  <td className="px-3 py-3">{requestCell(row)}</td>
                  <td className="px-3 py-2">{e.decision}</td>
                  <td className="px-3 py-2">{e.klass}</td>
                  <td className="px-3 py-2">{e.section}</td>
                  <td className="px-3 py-3">{statusCell(row)}</td>
                  <td className="px-3 py-2">{actions(row)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}
